<template>
  <teleport to="body">
    <!-- 遮罩层 -->
    <transition name="fade">
      <div v-if="visible" class="panel-overlay" @click="handleClose"></div>
    </transition>

    <!-- 侧边面板 -->
    <transition name="slide">
      <div v-if="visible" class="alarm-analysis-panel">
        <!-- 头部 -->
        <div class="panel-header">
          <h3>智能分析</h3>
          <button class="close-btn" @click="handleClose">
            <span>×</span>
          </button>
        </div>

        <!-- 内容区 -->
        <div class="panel-content">
          <!-- 告警概览 -->
          <section class="overview-section">
            <h4>告警概览</h4>
            <div class="info-list">
              <div class="info-item">
                <label>设备名称:</label>
                <span>{{ alarmData?.equipmentName || '-' }}</span>
              </div>
              <div class="info-item">
                <label>设备UUID:</label>
                <span>{{ alarmData?.uuid || '-' }}</span>
              </div>
              <div class="info-item">
                <label>告警编码:</label>
                <span>{{ alarmData?.alarmCode || '-' }}</span>
              </div>
              <div class="info-item">
                <label>告警类型:</label>
                <span>{{ alarmData?.description || '-' }}</span>
              </div>
              <div class="info-item">
                <label>告警等级:</label>
                <span :class="getLevelClass(alarmData?.level)">
                  {{ getLevelText(alarmData?.level) }}
                </span>
              </div>
              <div class="info-item">
                <label>告警状态:</label>
                <span :class="getStateClass(alarmData?.state)">
                  {{ getStateText(alarmData?.state) }}
                </span>
              </div>
              <div class="info-item">
                <label>开始时间:</label>
                <span>{{ alarmData?.startTime || '-' }}</span>
              </div>
              <div class="info-item" v-if="alarmData?.lastTime">
                <label>最后触发:</label>
                <span>{{ alarmData?.lastTime }}</span>
              </div>
              <div class="info-item" v-if="alarmData?.endTime">
                <label>结束时间:</label>
                <span>{{ alarmData?.endTime }}</span>
              </div>
              <div class="info-item">
                <label>触发次数:</label>
                <span class="highlight-text">{{ alarmData?.triggerCount || 0 }} 次</span>
              </div>
              <div class="info-item" v-if="alarmData?.tags?.length">
                <label>标签:</label>
                <span class="tags-wrapper">
                  <span v-for="(tag, idx) in alarmData.tags" :key="idx" class="tag-item">{{ tag }}</span>
                </span>
              </div>
            </div>
          </section>

          <!-- 智能摘要 -->
          <section class="summary-section">
            <h4>智能摘要</h4>

            <!-- 分析过程（loading时显示） -->
            <div v-if="loading && processSteps.length" class="process-loading">
              <div class="process-title">正在分析中...</div>
              <ol class="process-steps-live">
                <li v-for="(step, index) in processSteps" :key="index" :class="{
                  'step-completed': index < currentStep,
                  'step-active': index === currentStep,
                  'step-pending': index > currentStep
                }">
                  <span class="step-icon">
                    <span v-if="index < currentStep" class="icon-check">✓</span>
                    <span v-else-if="index === currentStep" class="icon-loading"></span>
                    <span v-else class="icon-pending">•</span>
                  </span>
                  <span class="step-text">{{ step.text }}</span>
                </li>
              </ol>
            </div>

            <!-- 普通加载状态 -->
            <div v-else-if="loading" class="loading-state">
              <div class="spinner"></div>
              <p>小奇智能体 正在分析中...</p>
            </div>

            <!-- 错误状态 -->
            <div v-else-if="error" class="error-state">
              <span class="error-icon">⚠️</span>
              <p>{{ error }}</p>
            </div>

            <!-- 分析结果 -->
            <div v-else-if="analysisResult?.summary" class="summary-content">
              {{ analysisResult.summary }}
            </div>

            <!-- 空状态 -->
            <div v-else class="empty-state">
              <p>暂无分析结果</p>
            </div>
          </section>

          <!-- 影响判断 -->
          <section v-if="analysisResult?.impact && !loading" class="impact-section">
            <h4>影响判断</h4>
            <div class="impact-content" :class="`impact-${analysisResult.impact.level}`">
              <div class="impact-level">
                <span class="impact-badge" :class="`badge-${analysisResult.impact.level}`">
                  {{ getImpactLevelText(analysisResult.impact.level) }}
                </span>
              </div>
              <p>{{ analysisResult.impact.description }}</p>
            </div>
          </section>

          <!-- 同类告警历史 -->
          <section v-if="analysisResult?.history?.length && !loading" class="history-section">
            <h4>同类告警历史</h4>
            <div class="history-items">
              <div v-for="item in analysisResult.history" :key="item.id" class="history-item">
                <span class="history-icon">🕐</span>
                <span class="history-text">{{ item.text }}</span>
                <span v-if="item.trend === 'up'" class="trend-badge">
                  趋势上升
                </span>
              </div>
            </div>
          </section>

          <!-- 建议关注 -->
          <section v-if="analysisResult?.suggestions?.length && !loading" class="suggestions-section">
            <h4>建议关注</h4>
            <ul class="suggestion-list">
              <li v-for="(item, idx) in analysisResult.suggestions" :key="idx">
                {{ item }}
              </li>
            </ul>
          </section>
        </div>

        <!-- 底部操作按钮 -->
        <div class="panel-footer">
          <button class="jump-btn" :disabled="loading" @click="jumpToFullAgent">
            <!-- <span class="icon">🤖</span> -->
            继续在智能体中深度分析
            <span class="arrow">→</span>
          </button>
        </div>
      </div>
    </transition>
  </teleport>
</template>

<script setup>
import { ref, watch, computed } from 'vue'
import { useRouter } from 'vue-router'
import { mockAlarmQuickAnalysis } from '@/mock/alarm-analysis'
import { getAlarmLevelText } from '../../dicts/alert'

const router = useRouter()

const props = defineProps({
  modelValue: {
    type: Boolean,
    default: false
  },
  alarmData: {
    type: Object,
    default: () => ({})
  },
  stationId: {
    type: String,
    default: ''
  },
  stationName: {
    type: String,
    default: ''
  }
})

const emit = defineEmits(['update:modelValue', 'jump-to-agent'])

// 面板显示状态
const visible = computed({
  get: () => props.modelValue,
  set: (val) => emit('update:modelValue', val)
})

// 加载状态
const loading = ref(false)
const error = ref('')
const analysisResult = ref(null)

// 当前分析步骤
const currentStep = ref(0)
const processSteps = ref([])

// 获取等级文本
const getLevelText = (level) => {
  return getAlarmLevelText(level)
}

// 获取等级样式类
const getLevelClass = (level) => {
  const levelMap = {
    0: 'level-info',
    1: 'level-low',
    2: 'level-medium',
    3: 'level-high',
    4: 'level-critical'
  }
  return levelMap[level] || 'level-info'
}

// 获取状态文本
const getStateText = (state) => {
  const stateMap = {
    1: '未恢复',
    2: '已恢复',
    3: '已确认',
    4: '已转工单',
    5: '已完成'
  }
  return stateMap[state] || '未知'
}

// 获取状态样式类
const getStateClass = (state) => {
  const stateClassMap = {
    1: 'state-unrecovered',
    2: 'state-recovered',
    3: 'state-confirmed',
    4: 'state-ticket',
    5: 'state-completed'
  }
  return stateClassMap[state] || ''
}

// 获取影响等级文本
const getImpactLevelText = (level) => {
  const map = {
    low: '低影响',
    medium: '中等影响',
    high: '高影响',
    critical: '严重影响'
  }
  return map[level] || '未知'
}

// 获取智能分析 - 使用 Mock 数据
const fetchAnalysis = async () => {
  if (!props.alarmData?.alarmId) {
    return
  }

  loading.value = true
  error.value = ''
  analysisResult.value = null
  currentStep.value = 0

  try {
    // 构造请求上下文
    const context = {
      source: 'alarm_detail',
      intent: 'alarm.analyze_summary',
      context: {
        station_id: props.stationId,
        station_name: props.stationName,
        alarm_id: props.alarmData.alarmId,
        device_id: props.alarmData.uuid,
        device_name: props.alarmData.equipmentName,
        alarm_code: props.alarmData.alarmCode,
        alarm_type: props.alarmData.description,
        alarm_level: props.alarmData.level,
        module_type: props.alarmData.moduleType,
        state: props.alarmData.state,
        start_time: props.alarmData.startTime,
        end_time: props.alarmData.endTime,
        last_time: props.alarmData.lastTime,
        trigger_count: props.alarmData.triggerCount,
        value: props.alarmData.value,
        tags: props.alarmData.tags
      }
    }

    // 使用 Mock 数据
    const result = await mockAlarmQuickAnalysis(context)

    if (result && result.code === 200) {
      // 如果返回了步骤信息，逐步展示
      if (result.data.processSteps?.length) {
        processSteps.value = result.data.processSteps

        // 逐步执行每个步骤
        for (let i = 0; i < processSteps.value.length; i++) {
          currentStep.value = i
          await new Promise(resolve => setTimeout(resolve, processSteps.value[i].duration))
        }
        currentStep.value = processSteps.value.length // 全部完成
      }

      analysisResult.value = result.data
    } else {
      throw new Error(result?.message || '获取分析结果失败')
    }
  } catch (err) {
    console.error('获取智能分析失败:', err)
    error.value = err.message || '分析服务暂时不可用，请稍后重试'
  } finally {
    loading.value = false
  }
}

// 关闭面板
const handleClose = () => {
  visible.value = false
}

// 跳转到完整智能体页面
const jumpToFullAgent = () => {
  // 构造完整上下文
  const context = {
    source: 'alarm_detail',
    intent: 'alarm.deep_analysis',
    context: {
      station_id: props.stationId,
      station_name: props.stationName,
      alarm_id: props.alarmData.alarmId,
      device_id: props.alarmData.uuid,
      device_name: props.alarmData.equipmentName,
      alarm_code: props.alarmData.alarmCode,
      alarm_type: props.alarmData.description,
      alarm_level: props.alarmData.level,
      module_type: props.alarmData.moduleType,
      state: props.alarmData.state,
      start_time: props.alarmData.startTime,
      end_time: props.alarmData.endTime,
      last_time: props.alarmData.lastTime,
      trigger_count: props.alarmData.triggerCount,
      value: props.alarmData.value,
      tags: props.alarmData.tags,
      // 携带已有的分析结果，避免重复计算
      quick_analysis: analysisResult.value
    }
  }

  // 新开标签页跳转到智能体页面
  const aiUrl = router.resolve({
    path: '/ai',
    query: {
      deviceUuid: props.alarmData.uuid,
      context: encodeURIComponent(JSON.stringify(context))
    }
  })

  window.open(aiUrl.href, '_blank')

  // 通知父组件
  emit('jump-to-agent', context)

  // 关闭面板
  visible.value = false
}

// 监听面板打开，自动获取分析
watch(() => props.modelValue, (newVal) => {
  if (newVal && props.alarmData?.alarmId) {
    fetchAnalysis()
  }
})
</script>

<style scoped lang="scss">
// 遮罩层动画
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.3s;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

// 面板滑入动画
.slide-enter-active,
.slide-leave-active {
  transition: transform 0.3s ease;
}

.slide-enter-from,
.slide-leave-to {
  transform: translateX(100%);
}

// 遮罩层
.panel-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  z-index: 999;
}

// 面板容器
.alarm-analysis-panel {
  position: fixed;
  right: 0;
  top: 0;
  bottom: 0;
  width: 680px;
  background: #fff;
  box-shadow: -4px 0 24px rgba(0, 0, 0, 0.12);
  z-index: 1000;
  display: flex;
  flex-direction: column;
}

.panel-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 20px 24px;
  border-bottom: 1px solid #f0f0f0;
  flex-shrink: 0;

  h3 {
    font-size: 18px;
    font-weight: 600;
    color: #1d2433;
    font-family: 'HarmonyOS Sans SC', sans-serif;
    margin: 0;
  }

  .close-btn {
    width: 32px;
    height: 32px;
    border: none;
    background: transparent;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 4px;
    transition: background 0.2s;

    &:hover {
      background: #f5f5f5;
    }

    span {
      font-size: 24px;
      color: #667085;
      line-height: 1;
    }
  }
}

.panel-content {
  flex: 1;
  padding: 24px;
  overflow-y: auto;
  overflow-x: hidden;

  section {
    margin-bottom: 24px;

    &:last-child {
      margin-bottom: 0;
    }

    h4 {
      font-size: 15px;
      font-weight: 600;
      color: #344054;
      margin: 0 0 12px 0;
      font-family: 'HarmonyOS Sans SC', sans-serif;
    }
  }
}

// 告警概览
.overview-section {
  .info-list {
    background: #fafbfc;
    border-radius: 8px;
    padding: 12px 16px;
  }

  .info-item {
    display: flex;
    padding: 8px 0;
    border-bottom: 1px dashed #e8eaed;

    &:last-child {
      border-bottom: none;
      padding-bottom: 0;
    }

    &:first-child {
      padding-top: 0;
    }

    label {
      width: 90px;
      color: #667085;
      font-size: 13px;
      font-family: 'HarmonyOS Sans SC', sans-serif;
      flex-shrink: 0;
    }

    span {
      flex: 1;
      color: #1d2433;
      font-size: 13px;
      font-weight: 500;
      font-family: 'HarmonyOS Sans SC', sans-serif;
      word-break: break-all;
    }

    .highlight-text {
      color: #3a68ff;
      font-weight: 600;
    }

    .level-info {
      color: #3a68ff;
    }

    .level-low {
      color: #52c41a;
    }

    .level-medium {
      color: #faad14;
    }

    .level-high {
      color: #ff8a66;
    }

    .level-critical {
      color: #f5222d;
    }

    .state-unrecovered {
      color: #f5222d;
      font-weight: 600;
    }

    .state-recovered {
      color: #52c41a;
    }

    .state-confirmed {
      color: #1890ff;
    }

    .state-ticket {
      color: #fa8c16;
    }

    .state-completed {
      color: #52c41a;
    }

    .tags-wrapper {
      display: flex;
      flex-wrap: wrap;
      gap: 6px;
    }

    .tag-item {
      padding: 2px 8px;
      background: #f0f5ff;
      color: #3a68ff;
      border-radius: 4px;
      font-size: 12px;
      font-weight: 500;
    }
  }
}

// 智能摘要
.summary-section {

  .loading-state,
  .error-state,
  .empty-state {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 32px 16px;
    text-align: center;

    p {
      margin: 12px 0 0 0;
      color: #667085;
      font-size: 13px;
    }
  }

  .spinner {
    width: 32px;
    height: 32px;
    border: 3px solid #f3f3f3;
    border-top: 3px solid #3a68ff;
    border-radius: 50%;
    animation: spin 1s linear infinite;
  }

  @keyframes spin {
    0% {
      transform: rotate(0deg);
    }

    100% {
      transform: rotate(360deg);
    }
  }

  .error-state {
    .error-icon {
      font-size: 32px;
    }

    p {
      color: #f5222d;
    }
  }

  .summary-content {
    padding: 16px;
    background: #f8faff;
    border-left: 4px solid #667eea;
    border-radius: 8px;
    color: #344054;
    font-size: 14px;
    line-height: 1.7;
    font-family: 'HarmonyOS Sans SC', sans-serif;
  }
}

// 分析过程（loading时实时显示）
.process-loading {
  padding: 16px;
  background: #f8faff;
  border-radius: 8px;
  // border-left: 4px solid #3a68ff;

  .process-title {
    font-size: 13px;
    font-weight: 600;
    color: #3a68ff;
    margin-bottom: 12px;
    font-family: 'HarmonyOS Sans SC', sans-serif;
  }

  .process-steps-live {
    margin: 0;
    padding: 0;
    list-style: none;

    li {
      display: flex;
      align-items: center;
      gap: 10px;
      padding: 8px 0;
      font-size: 13px;
      font-family: 'HarmonyOS Sans SC', sans-serif;
      transition: all 0.3s ease;

      .step-icon {
        width: 20px;
        height: 20px;
        display: flex;
        align-items: center;
        justify-content: center;
        flex-shrink: 0;
        border-radius: 50%;
        font-size: 12px;
      }

      .icon-check {
        color: #52c41a;
        font-weight: 600;
      }

      .icon-loading {
        width: 12px;
        height: 12px;
        border: 2px solid #3a68ff;
        border-top-color: transparent;
        border-radius: 50%;
        animation: spin 0.8s linear infinite;
      }

      .icon-pending {
        color: #d9d9d9;
        font-size: 10px;
      }

      .step-text {
        flex: 1;
      }

      &.step-completed {
        color: #52c41a;

        .step-icon {
          background: #f6ffed;
        }
      }

      &.step-active {
        color: #3a68ff;
        font-weight: 500;

        .step-icon {
          background: #e6f7ff;
        }
      }

      &.step-pending {
        color: #96a5b8;
      }
    }
  }
}

@keyframes spin {
  0% {
    transform: rotate(0deg);
  }

  100% {
    transform: rotate(360deg);
  }
}

// 影响判断
.impact-section {
  .impact-content {
    padding: 14px 16px;
    background: #fff4e5;
    border-left: 4px solid #ff8a66;
    border-radius: 8px;

    .impact-level {
      margin-bottom: 8px;
    }

    .impact-badge {
      display: inline-block;
      padding: 2px 8px;
      border-radius: 4px;
      font-size: 12px;
      font-weight: 500;

      &.badge-low {
        background: #e6f7ff;
        color: #1890ff;
      }

      &.badge-medium {
        background: #fff4e5;
        color: #fa8c16;
      }

      &.badge-high,
      &.badge-critical {
        background: #fff1f0;
        color: #f5222d;
      }
    }

    p {
      color: #b45309;
      font-size: 13px;
      line-height: 1.6;
      margin: 0;
      font-family: 'HarmonyOS Sans SC', sans-serif;
    }

    &.impact-low {
      background: #e6f7ff;
      border-left-color: #1890ff;

      p {
        color: #096dd9;
      }
    }

    &.impact-high,
    &.impact-critical {
      background: #fff1f0;
      border-left-color: #ff4d4f;

      p {
        color: #cf1322;
      }
    }
  }
}

// 同类告警历史
.history-section {
  .history-items {
    display: flex;
    flex-direction: column;
    gap: 10px;
  }

  .history-item {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 10px 12px;
    background: #fafbfc;
    border-radius: 6px;
    font-size: 13px;
    color: #344054;
    font-family: 'HarmonyOS Sans SC', sans-serif;

    .history-icon {
      flex-shrink: 0;
    }

    .history-text {
      flex: 1;
    }

    .trend-badge {
      flex-shrink: 0;
      padding: 2px 8px;
      background: #fff4e5;
      color: #fa8c16;
      border-radius: 4px;
      font-size: 11px;
      font-weight: 500;
    }
  }
}

// 建议关注
.suggestions-section {
  .suggestion-list {
    padding-left: 20px;
    margin: 0;

    li {
      color: #344054;
      font-size: 13px;
      line-height: 1.8;
      list-style-type: disc;
      font-family: 'HarmonyOS Sans SC', sans-serif;
      margin-bottom: 4px;

      &:last-child {
        margin-bottom: 0;
      }
    }
  }
}

// 底部操作按钮
.panel-footer {
  flex-shrink: 0;
  padding: 16px 24px;
  background: #fff;
  border-top: 1px solid #f0f0f0;

  .jump-btn {
    width: 100%;
    height: 48px;
    border: none;
    border-radius: 12px;
    background: #3a68ff;
    color: #fff;
    font-size: 14px;
    font-weight: 500;
    font-family: 'HarmonyOS Sans SC', sans-serif;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    transition: all 0.2s;

    &:hover:not(:disabled) {
      background: #2952cc;
      transform: translateY(-1px);
      box-shadow: 0 4px 12px rgba(58, 104, 255, 0.3);
    }

    &:disabled {
      background: #d9d9d9;
      cursor: not-allowed;
    }

    .icon {
      font-size: 18px;
    }

    .arrow {
      font-size: 16px;
      margin-left: 4px;
    }
  }
}

// 滚动条样式
.panel-content::-webkit-scrollbar {
  width: 6px;
}

.panel-content::-webkit-scrollbar-track {
  background: #f1f1f1;
  border-radius: 3px;
}

.panel-content::-webkit-scrollbar-thumb {
  background: #c1c1c1;
  border-radius: 3px;

  &:hover {
    background: #a8a8a8;
  }
}
</style>
